import React, { useState, useEffect } from 'react';
import { vehicleStatisticAPI, vehicleRegistrationAPI } from '../services/api';
import SearchableSelect from './SearchableSelect';
import VehicleStatisticTable from './VehicleStatisticTable';
import VehicleStatisticForm from './VehicleStatisticForm';
import Swal from 'sweetalert2';
import './VehicleStatistic.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://apiqldangkyxe.nguyenluan.vn';

const TABS = [
  { id: 'DUMP_TRUCK', label: 'Ô tô tải tự đổ < 15.000kg' },
  { id: 'LIMOUSINE', label: 'Xe Limousine' },
  { id: 'PASSENGER_NO_YELLOW', label: 'Xe KDVT chưa đổi biển vàng' },
  { id: 'WORKER_MOTO', label: 'Mô tô công nhân KCN' }
];

const VehicleStatisticManagement = ({ user }) => {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [completionFilter, setCompletionFilter] = useState(''); // '' | 'completed' | 'incomplete'
  const [units, setUnits] = useState([]); // Tất cả các đơn vị (dành cho form, import)
  const [filterUnits, setFilterUnits] = useState([]); // Đơn vị dùng cho bộ lọc thanh tìm kiếm
  const [exportingExcel, setExportingExcel] = useState(false);

  // Modal form states
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Import states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importCategory, setImportCategory] = useState(TABS[0].id);
  const [importUnit, setImportUnit] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  // Viewer states
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);

  useEffect(() => {
    if (user.role === 'admin' || user.role === 'supervisor') {
      fetchUnits();
    }
  }, [user.role]);

  useEffect(() => {
    if (user.role === 'admin' || user.role === 'supervisor') {
      fetchFilterUnits();
    }
  }, [user.role, activeTab, completionFilter]);

  useEffect(() => {
    fetchData();
  }, [activeTab, pagination.page, unitFilter, completionFilter]);

  const fetchUnits = async () => {
    try {
      const res = await vehicleRegistrationAPI.getUnits();
      const unitsData = res.data.units || res.data;
      setUnits(Array.isArray(unitsData) ? unitsData : []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const fetchFilterUnits = async () => {
    try {
      const params = { vehicleCategory: activeTab };
      if (completionFilter) {
        params.completionStatus = completionFilter;
      }
      const res = await vehicleStatisticAPI.getUnitsWithCompletionStatus(params);
      const unitsData = res.data.units || res.data;
      setFilterUnits(Array.isArray(unitsData) ? unitsData : []);
      
      // Nếu unit đang chọn không có trong list mới (khi lọc), reset unitFilter
      if (unitFilter && Array.isArray(unitsData)) {
        const stillExists = unitsData.find(u => u.id.toString() === unitFilter.toString());
        if (!stillExists) setUnitFilter('');
      }
    } catch (error) {
      console.error('Error fetching filter units:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await vehicleStatisticAPI.getStatistics({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        vehicleCategory: activeTab,
        unitId: unitFilter,
        completionStatus: completionFilter
      });
      setData(res.data.data);
      setSummary(res.data.summary);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Error fetching vehicle statistics:', error);
      Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Có lỗi khi tải danh sách thống kê' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchData();
  };

  const exportToExcel = async () => {
    try {
      setExportingExcel(true);
      const res = await vehicleStatisticAPI.exportExcel({
        vehicleCategory: activeTab,
        search: searchTerm,
        unitId: unitFilter,
        completionStatus: completionFilter
      });
      
      const tabLabel = TABS.find(t => t.id === activeTab).label.replace(/ /g, '_');
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Thong_Ke_${tabLabel}_${new Date().getTime()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      Swal.fire({ icon: 'success', title: 'Thành công', text: 'Đã tải xuống file Excel', timer: 2000, showConfirmButton: false });
    } catch (error) {
      console.error('Error exporting excel:', error);
      let errorMsg = 'Có lỗi khi xuất Excel!';
      
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.message || errorMsg;
        } catch (e) {
          // ignore parsing error
        }
      } else if (error.response && error.response.data && error.response.data.message) {
        errorMsg = error.response.data.message;
      }
      
      Swal.fire({ icon: 'warning', title: 'Thông báo', text: errorMsg });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      Swal.fire({ icon: 'warning', title: 'Thiếu thông tin', text: 'Vui lòng chọn file Excel' });
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('vehicleCategory', importCategory);
      
      if ((user.role === 'admin' || user.role === 'supervisor') && importUnit) {
        formData.append('unitId', importUnit);
      } else if ((user.role === 'admin' || user.role === 'supervisor') && !importUnit) {
        Swal.fire({ icon: 'warning', title: 'Thiếu thông tin', text: 'Vui lòng chọn đơn vị' });
        setImporting(false);
        return;
      }

      const response = await vehicleStatisticAPI.importStatistics(formData);
      setImportResult(response.data);
      setImportFile(null);
      fetchData();
      
      Swal.fire({ icon: 'success', title: 'Thành công!', text: 'Import dữ liệu thành công', timer: 2000, showConfirmButton: false });
    } catch (error) {
      console.error('Error importing:', error);
      Swal.fire({ icon: 'error', title: 'Lỗi!', text: error.response?.data?.message || 'Lỗi khi import file' });
    } finally {
      setImporting(false);
    }
  };

  const handleOpenAddForm = () => {
    setEditingItem(null);
    setShowFormModal(true);
  };

  const handleOpenEditForm = (item) => {
    setEditingItem(item);
    setShowFormModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Xác nhận xóa',
      text: 'Bạn có chắc chắn muốn xóa bản ghi này?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Đồng ý',
      cancelButtonText: 'Hủy'
    });

    if (result.isConfirmed) {
      try {
        await vehicleStatisticAPI.deleteStatistic(id);
        Swal.fire({ icon: 'success', title: 'Thành công', text: 'Đã xóa thành công!' });
        fetchData();
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Có lỗi khi xóa' });
      }
    }
  };

  const handleSaveForm = async (formData, newImageFiles, existingImages) => {
    try {
      const payload = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          payload.append(key, formData[key]);
        }
      });
      
      if (newImageFiles && newImageFiles.length > 0) {
        newImageFiles.forEach(file => {
          payload.append('sceneImages', file);
        });
      }
      
      if (existingImages) {
        payload.append('existingSceneImages', JSON.stringify(existingImages));
      }

      if (!editingItem) {
        payload.append('vehicleCategory', activeTab);
      }

      if (editingItem) {
        await vehicleStatisticAPI.updateStatistic(editingItem.id, payload);
        Swal.fire({ icon: 'success', title: 'Thành công', text: 'Cập nhật thành công!', timer: 2000, showConfirmButton: false });
      } else {
        await vehicleStatisticAPI.createStatistic(payload);
        Swal.fire({ icon: 'success', title: 'Thành công', text: 'Thêm mới thành công!', timer: 2000, showConfirmButton: false });
      }
      setShowFormModal(false);
      fetchData();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Có lỗi khi lưu dữ liệu' });
    }
  };

  return (
    <div className="vehicle-statistic-management">
      <div className="statistic-tabs">
        {TABS.map(tab => (
          <button 
            key={tab.id}
            className={`stat-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              setSummary(null);
              setCompletionFilter('');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="stat-toolbar">
        <div className="stat-filter-group">
          <form onSubmit={handleSearch} className="stat-search-form">
            <input 
              type="text" 
              placeholder="Tìm biển số, tên chủ sở hữu..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="stat-search-input"
            />
            <button type="submit" className="stat-btn stat-btn-search">🔍 Tìm kiếm</button>
          </form>

          {(user.role === 'admin' || user.role === 'supervisor') && (
            <div className="stat-select-unit">
              <SearchableSelect
                options={[
                  { value: '', label: 'Tất cả đơn vị' },
                  ...filterUnits.map(unit => ({
                    value: unit.id,
                    label: unit.name
                  }))
                ]}
                value={unitFilter}
                onChange={setUnitFilter}
                placeholder="Tất cả đơn vị"
              />
            </div>
          )}

          <select
            id="completionFilter"
            className="stat-select-status"
            value={completionFilter}
            onChange={(e) => {
              setCompletionFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{
              backgroundColor: completionFilter === 'completed' ? '#d1fae5' : completionFilter === 'incomplete' ? '#fee2e2' : '#ffffff',
              color: completionFilter === 'completed' ? '#065f46' : completionFilter === 'incomplete' ? '#7f1d1d' : '#374151',
              fontWeight: completionFilter ? '600' : '400'
            }}
          >
            <option value="">📋 Tất cả trạng thái</option>
            <option value="completed">✅ Đã nhập</option>
            <option value="incomplete">⚠️ Chưa nhập</option>
          </select>
        </div>
        
        <div className="stat-action-group">
          <button 
            className="stat-btn stat-btn-add" 
            onClick={handleOpenAddForm}
          >
            + Thêm mới
          </button>
          
          {(user.role === 'admin' || user.role === 'supervisor') && (
            <button 
              className="stat-btn stat-btn-import" 
              onClick={() => {
                setImportCategory(activeTab);
                setShowImportModal(true);
              }}
            >
              📥 Import Excel
            </button>
          )}
          
          <button 
            className="stat-btn stat-btn-export" 
            onClick={exportToExcel}
            disabled={exportingExcel}
          >
            {exportingExcel ? '⏳ Đang xuất...' : '📊 Xuất Excel'}
          </button>
        </div>
      </div>

      {summary && (
        <div className="statistic-summary-box">
          <div className="summary-item summary-total">
            <span className="summary-label">Tổng số xe</span>
            <span className="summary-value">{summary.total}</span>
          </div>
          <div
            className="summary-item"
            style={{ backgroundColor: '#d1fae5', border: '1.5px solid #6ee7b7', cursor: 'pointer', transition: 'opacity .15s' }}
            onClick={() => {
              const next = completionFilter === 'completed' ? '' : 'completed';
              setCompletionFilter(next);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            title="Nhấn để lọc Đã nhập"
          >
            <span className="summary-label" style={{ color: '#065f46' }}>✅ Đã nhập</span>
            <span className="summary-value" style={{ color: '#065f46' }}>{summary.completedCount ?? 0}</span>
          </div>
          <div
            className="summary-item"
            style={{ backgroundColor: '#fee2e2', border: '1.5px solid #fca5a5', cursor: 'pointer', transition: 'opacity .15s' }}
            onClick={() => {
              const next = completionFilter === 'incomplete' ? '' : 'incomplete';
              setCompletionFilter(next);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            title="Nhấn để lọc Chưa nhập"
          >
            <span className="summary-label" style={{ color: '#7f1d1d' }}>⚠️ Chưa nhập</span>
            <span className="summary-value" style={{ color: '#7f1d1d' }}>{summary.incompleteCount ?? 0}</span>
          </div>
          {(activeTab === 'DUMP_TRUCK' || activeTab === 'LIMOUSINE') && (
            <>
              <div className="summary-item summary-success">
                <span className="summary-label">Có GSHT</span>
                <span className="summary-value">{summary.hasMonitoringDevice}</span>
              </div>
              <div className="summary-item summary-danger">
                <span className="summary-label">Không GSHT</span>
                <span className="summary-value">{summary.noMonitoringDevice}</span>
              </div>
            </>
          )}
          {activeTab === 'PASSENGER_NO_YELLOW' && (
            <>
              <div className="summary-item summary-success">
                <span className="summary-label">Đã đổi biển vàng</span>
                <span className="summary-value">{summary.hasYellowPlate}</span>
              </div>
              <div className="summary-item summary-danger">
                <span className="summary-label">Chưa đổi biển vàng</span>
                <span className="summary-value">{summary.noYellowPlate}</span>
              </div>
            </>
          )}
          {activeTab === 'WORKER_MOTO' && (
            <>
              <div className="summary-item summary-success">
                <span className="summary-label">Chính chủ</span>
                <span className="summary-value">{summary.officialOwnership}</span>
              </div>
              <div className="summary-item summary-danger">
                <span className="summary-label">Không chính chủ</span>
                <span className="summary-value">{summary.unofficialOwnership}</span>
              </div>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading">Đang tải dữ liệu...</div>
      ) : (
        <VehicleStatisticTable 
          data={data}
          vehicleCategory={activeTab}
          userRole={user.role}
          page={pagination.page}
          limit={pagination.limit}
          onEdit={handleOpenEditForm}
          onDelete={handleDelete}
          onViewImages={(images) => {
            setViewerImages(images);
            setViewerIndex(0);
          }}
        />
      )}

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Trước
          </button>
          <span className="page-info">Trang {pagination.page} / {pagination.totalPages}</span>
          <button 
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Sau
          </button>
        </div>
      )}

      {showFormModal && (
        <VehicleStatisticForm
          vehicleCategory={activeTab}
          initialData={editingItem}
          onSave={handleSaveForm}
          onCancel={() => setShowFormModal(false)}
          user={user}
          units={units}
        />
      )}

      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header">
              <h3>Import từ Excel</h3>
              <button className="btn-close" onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportResult(null);
                setImportUnit('');
              }}>&times;</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Loại phương tiện (*):</label>
                <select 
                  value={importCategory} 
                  onChange={(e) => setImportCategory(e.target.value)}
                >
                  {TABS.map(tab => (
                    <option key={tab.id} value={tab.id}>{tab.label}</option>
                  ))}
                </select>
              </div>

              {(user.role === 'admin' || user.role === 'supervisor') && (
                <div className="form-group">
                  <label>Chọn đơn vị (*):</label>
                  <SearchableSelect
                    options={[
                      { value: '', label: '-- Chọn đơn vị --' },
                      ...units.map(unit => ({ value: unit.id, label: unit.name }))
                    ]}
                    value={importUnit}
                    onChange={setImportUnit}
                    placeholder="-- Chọn đơn vị --"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Chọn file Excel (*):</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', width: '100%' }}
                />
              </div>

              {importResult && (
                <div className="import-result" style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f3f4f6', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#374151' }}>Kết quả import:</h4>
                  <p style={{ margin: '5px 0' }}>Tổng số dòng đã quét: <strong>{importResult.summary.total}</strong></p>
                  <p style={{ margin: '5px 0', color: '#10b981' }}>Thành công: <strong>{importResult.summary.imported}</strong></p>
                  <p style={{ margin: '5px 0', color: '#f59e0b' }}>Trùng lặp biển số: <strong>{importResult.summary.duplicates}</strong></p>
                  <p style={{ margin: '5px 0', color: '#ef4444' }}>Lỗi khác: <strong>{importResult.summary.errors}</strong></p>
                </div>
              )}

              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportResult(null);
                  setImportUnit('');
                }}>
                  Đóng
                </button>
                <button
                  className="btn-primary"
                  onClick={handleImport}
                  disabled={!importFile || importing}
                >
                  {importing ? 'Đang import...' : 'Import dữ liệu'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewerImages.length > 0 && (
        <div 
          className="modal-overlay" 
          style={{ zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={() => setViewerImages([])}
        >
          <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <button 
              onClick={() => setViewerImages([])}
              style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', color: 'white', border: 'none', fontSize: '30px', cursor: 'pointer', zIndex: 10001 }}
            >×</button>

            {viewerImages.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setViewerIndex(prev => (prev === 0 ? viewerImages.length - 1 : prev - 1)); }}
                style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: '30px', padding: '10px 20px', cursor: 'pointer', borderRadius: '4px', zIndex: 10001, width: 'auto', display: 'inline-block' }}
              >
                ❮
              </button>
            )}

            <img 
              src={viewerImages[viewerIndex].startsWith('http') ? viewerImages[viewerIndex] : `${API_BASE_URL}${viewerImages[viewerIndex]}`} 
              alt={`View ${viewerIndex + 1}`} 
              style={{ maxWidth: '90%', maxHeight: '85vh', objectFit: 'contain' }} 
              onClick={(e) => e.stopPropagation()}
            />

            {viewerImages.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setViewerIndex(prev => (prev === viewerImages.length - 1 ? 0 : prev + 1)); }}
                style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: '30px', padding: '10px 20px', cursor: 'pointer', borderRadius: '4px', zIndex: 10001, width: 'auto', display: 'inline-block' }}
              >
                ❯
              </button>
            )}

            <div style={{ position: 'absolute', bottom: '20px', color: 'white', fontSize: '16px', background: 'rgba(0,0,0,0.5)', padding: '5px 15px', borderRadius: '20px' }}>
              {viewerIndex + 1} / {viewerImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleStatisticManagement;
